<template>
  <div class="roaming-rates-search">
    <!-- Search Input -->
    <div class="bg-primary p-8">
      <h2 class="text-2xl font-bold mb-6 text-white">Check roaming rates</h2>
      <div class="relative">
        <input
          type="text"
          v-model="searchQuery"
          placeholder="Where are you travelling to?"
          class="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:border-primary bg-white"
          :class="{ 'border-red-500': hasSpecialChars }"
          @input="validateAndShowResults"
          @blur="setTimeout(() => (showResults = false), 200)"
        />
        <!-- Validation Message for Special Characters -->
        <div v-if="hasSpecialChars && searchQuery" class="absolute w-full mt-1 p-3 bg-red-50 border border-red-200 text-red-600 rounded-md z-50">
          Please enter a valid country name without special characters.
        </div>
        
        <!-- No Results Message -->
        <div
          v-else-if="showResults && searchQuery && filteredCountries.length === 0 && !hasSpecialChars"
          class="absolute w-full mt-1 p-3 bg-gray-50 border border-gray-200 text-gray-600 rounded-md z-50"
        >
          No countries found matching "{{ searchQuery }}". Please try a different search term.
        </div>
        
        <!-- Search Results -->
        <div
          v-else-if="showResults && filteredCountries.length > 0"
          class="absolute w-full bg-white border border-gray-200 rounded-md mt-1 max-h-60 overflow-y-auto z-50"
        >
          <button
            v-for="country in filteredCountries"
            :key="country.id"
            @click="selectCountry(country)"
            class="w-full text-left p-3 hover:bg-blue-500 hover:text-white"
            :class="{ 'bg-blue-500 text-white': selectedCountry === country }"
          >
            {{ country.displayName }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";

// Props
const props = defineProps({
  tariffData: {
    type: Object,
    required: true,
  },
});

// Emits
const emit = defineEmits(["select-country"]);

// State
const searchQuery = ref("");
const showResults = ref(false);
const selectedCountry = ref(null);
const hasSpecialChars = ref(false);

// Computed
const allCountries = computed(() => {
  if (!props.tariffData?.zones) return [];
  return props.tariffData.zones.reduce((acc, zone) => {
    return [...acc, ...zone.countries];
  }, []);
});

const filteredCountries = computed(() => {
  if (!searchQuery.value) return [];
  const query = searchQuery.value.toLowerCase();
  return allCountries.value
    .filter((country) => country.displayName.toLowerCase().includes(query))
    .sort((a, b) => {
      // Exact matches first
      const aStartsWith = a.displayName.toLowerCase().startsWith(query);
      const bStartsWith = b.displayName.toLowerCase().startsWith(query);
      if (aStartsWith && !bStartsWith) return -1;
      if (!aStartsWith && bStartsWith) return 1;
      return a.displayName.localeCompare(b.displayName);
    });
});

// Methods
const validateAndShowResults = () => {
  // Check for special characters using regex (allow letters, numbers, spaces, and common punctuation)
  const specialCharsRegex = /[^a-zA-Z0-9\s\-,.()]/;
  hasSpecialChars.value = specialCharsRegex.test(searchQuery.value);
  
  // Only show results if there are no special characters
  showResults.value = searchQuery.value.length > 0;
};

const selectCountry = (country) => {
  selectedCountry.value = country;
  searchQuery.value = country.displayName;
  showResults.value = false;
  hasSpecialChars.value = false;
  emit("select-country", country);
};
</script>

<style scoped>
.roaming-rates-search {
  max-width: 1430px;
  margin: 0 auto;
}

/* Scrollbar Styling */
.overflow-y-auto {
  scrollbar-width: thin;
  scrollbar-color: #e5e7eb transparent;
}

.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: #e5e7eb;
  border-radius: 3px;
}
</style>
